import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useSetAllocation } from '@/query/queries/investing/useSetAllocation';
import { useGetPreAllocateInfo } from '@/query/queries/investing/useGetPreAllocateInfo';
import { useGetCalculatorConstants } from '@/query/queries/calculator/useGetCalculatorConstants';
import { Button, MoneyField, Snackbar } from '@scramble/ui';
import { prettyMoney } from '@scramble/utils';
import { Preloader } from '@/components/Preloader';
import { NewBrandsJoinedRoundModal } from '@/modals/NewBrandsJoinedRoundModal';
import { APP_LINKS } from '@/helpers/links';
import { withRouterBase } from '@/config/routerBase';
import { safeDecimal } from '@/helpers/number';
import { LOAN_GROUPS_IDS } from '@/helpers/constants';
import { useChat } from '@/hooks/useChat';
import AnalyticsGA from '@/analyticsGA';
import { useRoundStatus } from '@/hooks/shared/useRoundStatus';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { LayoutHeader } from '../layout/Header';
import { MoneyFieldPlaceholder } from '../MoneyFieldPlaceholder';
import { useAllocationStatus } from '../../hooks/useAllocationStatus';
import { useConfirmedAllocationStatus } from '@/hooks/shared/useConfirmedAllocationStatus';
import {
  useEveryFiveGroupA,
  LAYOUT_TYPE as LayoutEvery,
} from '@/containers/EveryFiveGroupA';
import {
  useIncreasedIncome,
  LAYOUT_TYPE as LayoutIncreased,
} from '@/containers/IncreasedIncome';
import { Balance } from './Balance';
import { useAllocationBalance } from './Balance/useAllocationBalance';
import { BrandSelection } from './BrandSelection';
import { getValidationSchema } from './schema';
import { TERM_TOOLTIPS } from './loan_tooltips';
import IconHelp from '@scramble/design-system/icons/help-circle.svg';
import IconFill from '@scramble/design-system/icons/arrow-circle-down-left.svg';
import l from '../layout/layout.module.scss';
import s from './index.module.scss';

export const StepAllocation = ({ wizardId, onBack, onNext, setLoading, savedAllocation, onAllocationSaved }) => {
  const { status, isParticipating, addOnly } = useRoundStatus();
  const { t } = useTranslation();
  const { mutateAsync: setAllocation } = useSetAllocation();
  const { openSingleModal } = useModal();
  const [newBrandsModalData, setNewBrandsModalData] = useState(null);
  const { refetch: getIntermediateInfo } = useGetPreAllocateInfo();
  const { data: calcConstants } = useGetCalculatorConstants();
  const TERM_TOOLTIPS_T = TERM_TOOLTIPS(t);

  useChat(false);

  useEffect(() => {
    getIntermediateInfo();
    AnalyticsGA.investingWizardStep1Show({ wizard_id: wizardId });
  }, []);

  const {
    preAllocateInfo,
    allocatedGroupA,
    allocatedGroupB,
    bonusAmount,
    totalBonusAmount,
    remainingAmountGroupA,
    remainingAmountGroupB,
    personalRemainingAmountGroupA,
    personalRemainingAmountGroupB,
    hasNewBrandsWithCapacityGroupA,
    hasNewBrandsWithCapacityGroupB,
    totalRemainingAmountGroupA,
    totalRemainingAmountGroupB,
    totalCash,
    isLoading,
  } = useAllocationStatus();

  const noCash = (totalCash - totalBonusAmount) < 0.1;

  const {
    allocatedGroupA: confirmedAllocationGroupA,
    allocatedGroupB: confirmedAllocationGroupB,
    allocatedBonusAmount: confirmedBonusAmount,
  } = useConfirmedAllocationStatus();
  const maxGroupAAmount = safeDecimal(allocatedGroupA)
    .add(safeDecimal(remainingAmountGroupA))
    .toNumber();
  const maxGroupBAmount = safeDecimal(allocatedGroupB)
    .add(safeDecimal(remainingAmountGroupB))
    .toNumber();

  const submitAllocation = async payload => {
    setLoading(() => true);
    const res = await setAllocation(payload);
    if (res?.success) {
      onAllocationSaved?.(payload);
      onNext();
    }
    setLoading(() => false);
    return !!res?.success;
  };

  const getNewBrandsJoinedGroups = allocation => {
    const groupConfigs = [
      {
        group: LOAN_GROUPS_IDS.conservative.id,
        field: 'group_a_amount',
        personalRemainingAmount: personalRemainingAmountGroupA,
        hasNewBrandsWithCapacity: hasNewBrandsWithCapacityGroupA,
      },
      {
        group: LOAN_GROUPS_IDS.moderate.id,
        field: 'group_b_amount',
        personalRemainingAmount: personalRemainingAmountGroupB,
        hasNewBrandsWithCapacity: hasNewBrandsWithCapacityGroupB,
      },
    ];

    return groupConfigs.reduce((res, item) => {
      const enteredAmount = safeDecimal(allocation[item.field]);
      const newBrandsAmount = enteredAmount.minus(item.personalRemainingAmount);

      if (newBrandsAmount.greaterThan(0) && item.hasNewBrandsWithCapacity) {
        res.push({
          group: item.group,
          amount: newBrandsAmount.toNumber(),
        });
      }

      return res;
    }, []);
  };

  const onSubmit = async values => {
    const totalInvestment =
      Number(values.group_a_amount || 0) + Number(values.group_b_amount || 0);
    if (totalInvestment < status?.min_investment_amount) {
      return Snackbar({
        type: 'danger',
        text: t('investing:allocate_form.min_investment_amount', {
          amount: prettyMoney(status?.min_investment_amount),
        }),
      });
    }

    const payload = {
      group_a_amount: values.group_a_amount,
      group_b_amount: values.group_b_amount,
    };

    const newBrandsJoinedGroups = getNewBrandsJoinedGroups(payload);

    if (newBrandsJoinedGroups.length) {
      setNewBrandsModalData({
        groups: newBrandsJoinedGroups,
        payload,
      });
      openSingleModal(MODAL_IDS.new_brands_joined_round);
      return;
    }

    await submitAllocation(payload);
  };

  const { values, errors, touched, setFieldValue, handleSubmit } = useFormik({
    enableReinitialize: true,
    validationSchema: getValidationSchema({
      min_group_a: remainingAmountGroupA > 0
        ? (addOnly ? confirmedAllocationGroupA + bonusAmount : totalBonusAmount)
        : (addOnly ? confirmedAllocationGroupA : 0),
      min_group_b: addOnly ? confirmedAllocationGroupB : 0,
      max_group_a: maxGroupAAmount,
      max_group_b: maxGroupBAmount,
      addOnly,
      min_total: status?.min_investment_amount,
      total_cash_without_bonus: totalCash - totalBonusAmount,
      total_bonus_amount: totalBonusAmount,
    }),
    initialValues: {
      group_a_amount: savedAllocation ? savedAllocation.group_a_amount : allocatedGroupA,
      group_b_amount: savedAllocation ? savedAllocation.group_b_amount : allocatedGroupB,
    },
    onSubmit,
  });

  const { availableCashAmount, availableBonusAmount } = useAllocationBalance({
    allocatedGroupA: values.group_a_amount,
    allocatedGroupB: values.group_b_amount,
    confirmedAllocationGroupA,
    confirmedAllocationGroupB,
    confirmedBonusAmount,
  });

  const IncreasedIncome = useIncreasedIncome({
    layout: LayoutIncreased.investing_offer_card,
  });
  const EveryFiveGroupA = useEveryFiveGroupA({
    amount: values.group_a_amount || 0,
    bonusAmount: totalBonusAmount,
    layout: LayoutEvery.investing_offer_card,
  });

  const getMessage = ({ amount, bonus, groupTitle, remainingAmount }) => {
    if (bonus && amount) {
      const diff = Number(amount) - Number(bonus) || 0;
      return t('investing:step_allocation.allocated_bonus_cash', {
        bonus: prettyMoney(diff <= 0 ? amount : bonus),
        cash: prettyMoney(diff <= 0 ? 0 : diff),
      });
    }

    if (Number(amount)) {
      return t('investing:step_allocation.allocated_cash', {
        cash: prettyMoney(amount),
      });
    }

    return t('investing:step_allocation.remains_to_be_raised', {
      groupTitle,
      remainingAmount: prettyMoney(remainingAmount),
    });
  };

  const groupAFullForInvestor =
    (addOnly
      ? confirmedAllocationGroupA >= personalRemainingAmountGroupA
      : personalRemainingAmountGroupA === 0) && !hasNewBrandsWithCapacityGroupA;

  const groupBFullForInvestor =
    (addOnly
      ? confirmedAllocationGroupB >= personalRemainingAmountGroupB
      : personalRemainingAmountGroupB === 0) && !hasNewBrandsWithCapacityGroupB;

  const blockedGroupA = groupAFullForInvestor;
  const blockedGroupB = groupBFullForInvestor || noCash;

  const getAvailableAmountForGroup = group => {
    const currentAmount =
      group === LOAN_GROUPS_IDS.conservative.id
        ? values.group_a_amount
        : values.group_b_amount;
    const maxAmount =
      group === LOAN_GROUPS_IDS.conservative.id
        ? maxGroupAAmount
        : maxGroupBAmount;
    const availableAmount =
      group === LOAN_GROUPS_IDS.conservative.id
        ? safeDecimal(availableCashAmount).add(availableBonusAmount)
        : safeDecimal(availableCashAmount);
    const groupRemainingAmount = safeDecimal(maxAmount).sub(
      safeDecimal(currentAmount),
    );

    if (availableAmount.lte(0) || groupRemainingAmount.lte(0)) {
      return 0;
    }

    return (
      availableAmount.lessThan(groupRemainingAmount)
        ? availableAmount
        : groupRemainingAmount
    ).toNumber();
  };

  const availableAmountGroupA = getAvailableAmountForGroup(
    LOAN_GROUPS_IDS.conservative.id,
  );
  const availableAmountGroupB = getAvailableAmountForGroup(
    LOAN_GROUPS_IDS.moderate.id,
  );
  const showAddAvailableGroupA = !blockedGroupA && availableAmountGroupA > 0;
  const showAddAvailableGroupB = !blockedGroupB && availableAmountGroupB > 0;

  const changeAllocationAmount = (field, value) => {
    setFieldValue(field, value);
  };

  const addAvailableAmount = (field, amount) => {
    const nextAmount = safeDecimal(values[field])
      .add(safeDecimal(amount))
      .toNumber();

    setFieldValue(field, nextAmount);
  };

  return (
    <form className={l.wrapper} onSubmit={handleSubmit}>
      <LayoutHeader onBack={onBack} />
      <div className={classnames(l.container)}>
        <div className={l.content}>
          <div className={s.form}>
            <Balance
              isLoading={isLoading}
              allocatedGroupA={values.group_a_amount}
              allocatedGroupB={values.group_b_amount}
              confirmedAllocationGroupA={confirmedAllocationGroupA}
              confirmedAllocationGroupB={confirmedAllocationGroupB}
              confirmedBonusAmount={confirmedBonusAmount}
            />
            {isLoading ? (
              <Preloader />
            ) : (
              <div className={s.allocation}>
                <div className={s.header}>
                  <h2 className={s.title}>
                    {isParticipating
                      ? t('investing:step_allocation.make_changes')
                      : t('investing:step_allocation.allocate_money')}
                  </h2>
                  <Button
                    theme="neutral"
                    appearance="tonal"
                    iconUrl={IconHelp}
                    size="md"
                    onClick={() =>
                      window.open(
                        withRouterBase(APP_LINKS.helpLoanTerms()),
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  />
                </div>
                <div className={s.groups}>
                  <div className={s.group}>
                    {blockedGroupA ? (
                      <MoneyFieldPlaceholder
                        label={t('investing:step_allocation.group_a')}
                        placeholder={prettyMoney(values.group_a_amount)}
                        description={t(
                          'investing:step_allocation.full_amount_raised',
                          {
                            group: 'A',
                          },
                        )}
                      />
                    ) : (
                      <div className={s.money_field}>
                        <MoneyField
                          isFloat
                          label={t('investing:step_allocation.group_a')}
                          name="group_a_amount"
                          value={values.group_a_amount}
                          onChange={value =>
                            changeAllocationAmount('group_a_amount', value)
                          }
                          description={
                            getMessage({
                              amount: Number(values.group_a_amount),
                              bonus: totalBonusAmount,
                              groupTitle: t(
                                'investing:step_allocation.group_a',
                              ),
                              remainingAmount: totalRemainingAmountGroupA,
                            }) || ' '
                          }
                          error={
                            touched.group_a_amount && errors.group_a_amount
                          }
                          onClear={() =>
                            changeAllocationAmount('group_a_amount', '0')
                          }
                        />
                        {showAddAvailableGroupA &&
                          availableAmountGroupA >= 0.01 && (
                            <Button
                              theme="neutral"
                              appearance="tonal"
                              size="lg"
                              iconUrl={IconFill}
                              onClick={() =>
                                addAvailableAmount(
                                  'group_a_amount',
                                  availableAmountGroupA,
                                )
                              }
                            />
                          )}
                      </div>
                    )}
                    <ul className={s.list}>
                      <li>
                        {TERM_TOOLTIPS_T.net_annual_return()}
                        {t('investing:step_allocation.target_annual_return_a', {
                          percent: `${calcConstants?.irr_group_a}%`,
                        })}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.earnings()}
                        {t('investing:step_allocation.flat_fee_1')}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.term()}
                        {t('investing:step_allocation.terms_6_months')}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.repayments()}
                        {t('investing:step_allocation.monthly_repayments')}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.protection(
                          LOAN_GROUPS_IDS.conservative.id,
                        )}
                        {t('investing:step_allocation.high_protection')}
                      </li>
                    </ul>
                  </div>
                  <div className={s.group}>
                    {blockedGroupB ? (
                      <MoneyFieldPlaceholder
                        label={t('investing:step_allocation.group_b')}
                        placeholder={prettyMoney(values.group_b_amount)}
                        description={
                          noCash
                            ? t('investing:step_allocation.bonus_only_for_group_a')
                            : t('investing:step_allocation.full_amount_raised', {
                                group: 'B',
                              })
                        }
                      />
                    ) : (
                      <div className={s.money_field}>
                        <MoneyField
                          isFloat
                          label={t('investing:step_allocation.group_b')}
                          name="group_b_amount"
                          value={values.group_b_amount}
                          onChange={value =>
                            changeAllocationAmount('group_b_amount', value)
                          }
                          description={
                            getMessage({
                              amount: Number(values.group_b_amount),
                              groupTitle: t(
                                'investing:step_allocation.group_b',
                              ),
                              remainingAmount: totalRemainingAmountGroupB,
                            }) || ' '
                          }
                          error={
                            touched.group_b_amount && errors.group_b_amount
                          }
                          onClear={() =>
                            changeAllocationAmount('group_b_amount', '0')
                          }
                        />
                        {showAddAvailableGroupB &&
                          availableAmountGroupB >=
                            0.01 && (
                              <Button
                                theme="neutral"
                                appearance="tonal"
                                size="lg"
                                iconUrl={IconFill}
                                onClick={() =>
                                  addAvailableAmount(
                                    'group_b_amount',
                                    availableAmountGroupB,
                                  )
                                }
                              />
                            )}
                      </div>
                    )}
                    <ul className={s.list}>
                      <li>
                        {TERM_TOOLTIPS_T.net_annual_return()}
                        {t('investing:step_allocation.target_annual_return_b', {
                          percent: `${calcConstants?.irr_group_b}%`,
                        })}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.earnings()}
                        {t('investing:step_allocation.flat_fee_9')}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.term()}
                        {t('investing:step_allocation.terms_6_months')}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.repayments()}
                        {t('investing:step_allocation.single_repayment')}
                      </li>
                      <li>
                        {TERM_TOOLTIPS_T.protection(
                          LOAN_GROUPS_IDS.moderate.id,
                        )}
                        {t('investing:step_allocation.medium_protection')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {(EveryFiveGroupA || IncreasedIncome) && (
              <div className={s.bonuses}>
                <div className={s.bonuses_title}>
                  {t('investing:step_allocation.bonuses_offers')}
                </div>
                <div className={s.bonuses_list}>
                  {EveryFiveGroupA}
                  {IncreasedIncome}
                </div>
              </div>
            )}
            <BrandSelection wizardId={wizardId} />
          </div>
        </div>
      </div>
      <div className={l.footer}>
        <div className={l.footer_container}>
          <div className={s.footer_content}>
            <span className="f-body-sm">
              {t('investing:step_allocation.change_reset_investment')}
            </span>
            <Button
              theme="brand"
              appearance="filled"
              type="submit"
              size="lg"
              disabled={!preAllocateInfo}
              className={s.btn_submit}
            >
              {t('investing:step_allocation.next')}
            </Button>
          </div>
        </div>
      </div>
      <NewBrandsJoinedRoundModal
        groups={newBrandsModalData?.groups}
        onCancel={() => setNewBrandsModalData(null)}
        onContinue={async () => {
          if (!newBrandsModalData?.payload) return false;

          const completed = await submitAllocation(newBrandsModalData?.payload);
          if (completed) {
            setNewBrandsModalData(null);
          }
          return completed;
        }}
      />
    </form>
  );
};
